import React from 'react'
import classNames from 'classnames/bind'
import { Head, Link } from '@inertiajs/react'
import { router } from '@inertiajs/react'
import AdminLayout from '@/admin/shell/layouts/AdminLayout'
import DataTable from '@/admin/shared/components/DataTable/DataTable'
import { TableSearch } from '@/admin/shared/components/DataTable/Toolbar'
import Badge from '@/admin/shared/components/Badge/Badge'
import { EVENT_COLUMNS } from '../components/AnalyticsEventsTable'
import type { AnalyticsSessionPageProps } from '../types'
import styles from '../analytics.module.scss'

const cx = classNames.bind(styles)

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(started: string | null, lastSeen: string | null): string {
  if (!started || !lastSeen) return '—'
  const diff = new Date(lastSeen).getTime() - new Date(started).getTime()
  if (diff < 0) return '—'
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remMin = minutes % 60
  if (hours < 24) return `${hours}h ${remMin}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

function SummaryItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={cx('summaryItem')}>
      <span className={cx('summaryLabel')}>{label}</span>
      <span className={cx('summaryValue')}>{children}</span>
    </div>
  )
}

function AnalyticsSessionPage({ session, events, eventTypes, search, eventType }: AnalyticsSessionPageProps) {
  const navigate = (params: Record<string, string | number | undefined>) =>
    router.get(`/admin/analytics/${session.id}`, params as Record<string, string>, { preserveState: true, replace: true })

  return (
    <>
      <Head title="Admin — Analytics Session" />
      <Link href="/admin/analytics" className={cx('backLink')}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Back to sessions
      </Link>

      <div className={cx('summaryCard')}>
        <div className={cx('summaryGrid')}>
          <SummaryItem label="Session ID">{session.session_id}</SummaryItem>
          <SummaryItem label="Anonymous ID">{session.anonymous_id}</SummaryItem>
          {session.user && (
            <SummaryItem label="User">
              <Link href={`/admin/users/${session.user.id}`}>{session.user.name}</Link>
            </SummaryItem>
          )}
          <SummaryItem label="Device">
            {session.device_type ?? '—'} · {session.browser ?? '—'} · {session.os ?? '—'}
          </SummaryItem>
          <SummaryItem label="Source">{session.source ?? '—'}</SummaryItem>
          <SummaryItem label="IP">{session.ip ?? '—'}</SummaryItem>
          <SummaryItem label="User Agent">{session.user_agent ?? '—'}</SummaryItem>
          <SummaryItem label="Bot">
            <Badge variant={session.is_bot ? 'warning' : 'muted'} dot>
              {session.is_bot ? 'Bot' : 'Human'}
            </Badge>
          </SummaryItem>
          <SummaryItem label="Started">{formatDateTime(session.started_at)}</SummaryItem>
          <SummaryItem label="Last Seen">{formatDateTime(session.last_seen_at)}</SummaryItem>
          <SummaryItem label="Duration">{formatDuration(session.started_at, session.last_seen_at)}</SummaryItem>
          <SummaryItem label="Landing Path">{session.landing_path ?? '—'}</SummaryItem>
          <SummaryItem label="Landing URL">{session.landing_url ?? '—'}</SummaryItem>
          <SummaryItem label="Referrer">{session.referrer ?? '—'}</SummaryItem>
          {session.utm_source && <SummaryItem label="UTM Source">{session.utm_source}</SummaryItem>}
          {session.utm_medium && <SummaryItem label="UTM Medium">{session.utm_medium}</SummaryItem>}
          {session.utm_campaign && <SummaryItem label="UTM Campaign">{session.utm_campaign}</SummaryItem>}
        </div>
      </div>

      <DataTable
        columns={EVENT_COLUMNS}
        rows={events.data}
        total={events.total}
        page={events.current_page}
        perPage={events.per_page}
        lastPage={events.last_page}
        toolbarLeft={
          <>
            <TableSearch
              value={search}
              placeholder="Search by event type or page path…"
              onChange={q => navigate({ search: q || undefined, page: 1, event_type: eventType })}
            />
            <select
              className={cx('filterSelect')}
              value={eventType ?? ''}
              onChange={e => navigate({ event_type: e.target.value || undefined, page: 1, search })}
            >
              <option value="">All event types</option>
              {eventTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </>
        }
        onPageChange={p => navigate({ search, page: p, event_type: eventType })}
        onPerPageChange={n => navigate({ search, per_page: n, page: 1, event_type: eventType })}
        emptyMessage="No events found for this session."
        selectable={false}
      />
    </>
  )
}

AnalyticsSessionPage.layout = (page: React.ReactNode) => (
  <AdminLayout title="Analytics Session">{page}</AdminLayout>
)

export default AnalyticsSessionPage
